import { Users, Shield } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { useFactionsByParty } from '@/hooks/useFactionsByParty'
import { FactionListCompact } from '@/components/faction/FactionList'
import { FactionSatisfactionBar } from '@/components/faction/FactionSatisfactionBar'

export interface FactionsSectionProps {
  partyId: string
  onSelectFaction?: (factionId: string) => void
}

export function FactionsSection({ partyId, onSelectFaction }: FactionsSectionProps) {
  const { factions, isLoading } = useFactionsByParty(partyId)

  // Calculate quick stats
  const factionCount = factions?.length ?? 0
  const demandsCount = factions?.filter(f => f.demand_type).length ?? 0
  const lowestSatisfaction = factions?.length
    ? Math.min(...factions.map(f => f.satisfaction))
    : null

  if (isLoading) {
    return (
      <Card className="animate-pulse">
        <CardHeader className="pb-2">
          <div className="h-5 bg-muted rounded w-32" />
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="h-8 bg-muted rounded" />
            <div className="h-8 bg-muted rounded" />
            <div className="h-8 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    )
  }

  // No factions - show positive message
  if (!factions || factions.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4" />
            Internal Factions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 py-4">
            <div className="p-2 rounded-full bg-green-500/10">
              <Shield className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="font-medium text-sm text-green-600 dark:text-green-400">
                Party unity is strong
              </p>
              <p className="text-xs text-muted-foreground">
                No internal factions have formed
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4" />
            Internal Factions
          </CardTitle>
          <span className="text-xs text-muted-foreground">
            {factionCount} {factionCount === 1 ? 'faction' : 'factions'}
          </span>
        </div>
        {(demandsCount > 0 || (lowestSatisfaction !== null && lowestSatisfaction < 40)) && (
          <CardDescription className="text-amber-500">
            {demandsCount > 0 && (
              <span>{demandsCount} active {demandsCount === 1 ? 'demand' : 'demands'}</span>
            )}
            {demandsCount > 0 && lowestSatisfaction !== null && lowestSatisfaction < 40 && ' • '}
            {lowestSatisfaction !== null && lowestSatisfaction < 40 && (
              <span>Lowest satisfaction: {lowestSatisfaction}%</span>
            )}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Quick stats */}
        {lowestSatisfaction !== null && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Lowest:</span>
            <FactionSatisfactionBar
              satisfaction={lowestSatisfaction}
              size="sm"
              showThresholds={false}
              showLabel={true}
              animated={false}
            />
          </div>
        )}

        {/* Faction list */}
        <FactionListCompact
          partyId={partyId}
          onSelectFaction={onSelectFaction}
          maxItems={4}
        />

        {factions.length > 4 && (
          <button
            className="w-full text-xs text-muted-foreground hover:text-foreground py-1"
            onClick={() => onSelectFaction?.(factions[0].id)}
          >
            View all {factions.length} factions →
          </button>
        )}
      </CardContent>
    </Card>
  )
}
